import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getBaseUrl } from "../utils";
import ExamAlertDetailModal, { StatusBadge, formatDate } from "./ExamAlertDetailModal";

const PAGE_SIZE = 10;
const FILTERS = [
    { value: "", label: "All" },
    { value: "Open", label: "Open" },
    { value: "Upcoming", label: "Upcoming" },
    { value: "Close", label: "Closed" },
];

const ExamAlertPage = () => {
    const baseUrl = getBaseUrl();
    const [alerts, setAlerts] = useState([]);
    const [status, setStatus] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);

    const fetchAlerts = useCallback(
        async (pageToLoad, statusFilter) => {
            setLoading(true);
            setError("");
            try {
                const token = localStorage.getItem("userToken");
                const response = await axios.get(`${baseUrl}user_api/examAlerts`, {
                    params: { page: pageToLoad, limit: PAGE_SIZE, status: statusFilter },
                    headers: { Authorization: `Bearer ${token}` },
                });
                const results = response.data?.results || [];
                setAlerts((prev) => (pageToLoad === 1 ? results : [...prev, ...results]));
                setTotalPages(response.data?.pagination?.totalPages || 1);
                setPage(pageToLoad);
            } catch (err) {
                console.error("Error fetching exam alerts:", err);
                setError("Could not load exam alerts. Please try again.");
            } finally {
                setLoading(false);
            }
        },
        [baseUrl]
    );

    useEffect(() => {
        fetchAlerts(1, status);
    }, [status, fetchAlerts]);

    return (
        <div className="p-4 md:p-6">
            {/* Status filter */}
            <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label="Filter exam alerts by status">
                {FILTERS.map((f) => (
                    <button
                        key={f.label}
                        type="button"
                        role="tab"
                        aria-selected={status === f.value}
                        onClick={() => setStatus(f.value)}
                        className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition ${
                            status === f.value
                                ? "bg-[#28282B] text-white border-[#28282B]"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

            {!loading && alerts.length === 0 && !error ? (
                <p className="text-gray-600">No exam alerts found for your class.</p>
            ) : (
                <div className="flex flex-col gap-4">
                    {alerts.map((alert) => (
                        <button
                            key={alert._id}
                            type="button"
                            onClick={() => setSelected(alert)}
                            className="w-full text-left bg-[#F9F9F9] hover:bg-[#F2F2F2] rounded-lg p-4 flex items-start gap-4 transition focus:outline-none focus:ring-2 focus:ring-[#28282B]"
                        >
                            <img
                                src={alert.image ? String(alert.image).replace(/\\/g, "/") : "/assates/Logo__newimage-removebg-preview.png"}
                                alt=""
                                className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover shrink-0 bg-white"
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/assates/Logo__newimage-removebg-preview.png";
                                }}
                            />
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h3 className="text-base md:text-lg font-bold m-0 break-words">
                                        {alert.course_or_exam || "Exam Alert"}
                                    </h3>
                                    <StatusBadge status={alert.status} />
                                </div>
                                {alert.description && (
                                    <p className="text-sm text-gray-600 m-0 line-clamp-2">{alert.description}</p>
                                )}
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                                    {alert.end_application && (
                                        <span>Last date to apply: {formatDate(alert.end_application)}</span>
                                    )}
                                    {alert.examination_date && (
                                        <span>Exam date: {formatDate(alert.examination_date)}</span>
                                    )}
                                    {!alert.end_application && !alert.examination_date && alert.Created_date && (
                                        <span>Posted on {formatDate(alert.Created_date)}</span>
                                    )}
                                </div>
                                <span className="inline-block mt-2 text-xs font-semibold text-[#0053C0]">
                                    View details <i className="ri-arrow-right-s-line align-middle"></i>
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {loading && <p className="text-gray-500 text-sm mt-4">Loading...</p>}

            {!loading && page < totalPages && (
                <div className="flex justify-center mt-6">
                    <button
                        type="button"
                        onClick={() => fetchAlerts(page + 1, status)}
                        className="px-5 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50"
                    >
                        Load more
                    </button>
                </div>
            )}

            {selected && <ExamAlertDetailModal alert={selected} onClose={() => setSelected(null)} />}
        </div>
    );
};

export default ExamAlertPage;
